
import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Edit2, Trash2, X, Save, Briefcase, 
  DollarSign, Info, ChevronRight, ChevronLeft, 
  Eye, Loader2, LayoutList, ClipboardList, Tag, ArrowLeft,
  FileText, Sparkles, ShieldCheck, Link as LinkIcon, ExternalLink
} from 'lucide-react';
import { Service, AgreementTemplate } from '../types';
import { supabase, toSnakeCase, toCamelCase, getErrorMessage } from '../lib/supabase';

interface ServicesProps {
  services: Service[];
  setServices: React.Dispatch<React.SetStateAction<Service[]>>;
}

const Services: React.FC<ServicesProps> = ({ services, setServices }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<AgreementTemplate[]>([]);
  
  const [formData, setFormData] = useState<Partial<Service>>({
    name: '',
    description: '',
    basePrice: 350,
    agreementTemplateId: ''
  });

  useEffect(() => {
    supabase.from('agreement_templates').select('*').then(({ data }) => {
       if (data) setTemplates(data.map(toCamelCase));
    });
  }, []);

  const filtered = services.filter(s => 
    (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (s.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedService = services.find(s => s.id === selectedServiceId);

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({ 
      name: '', 
      description: '', 
      basePrice: 350,
      agreementTemplateId: ''
    });
    setIsSidebarOpen(true);
  };

  const handleOpenEdit = (service: Service, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingId(service.id);
    setFormData({ ...service });
    setIsSidebarOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    setSaving(true);

    try {
      const payload = toSnakeCase(formData);
      if (editingId) {
        const { error } = await supabase.from('services').update(payload).eq('id', editingId);
        if (error) throw error;
        setServices(prev => prev.map(s => s.id === editingId ? { ...s, ...formData } as Service : s));
      } else {
        const { data, error } = await supabase.from('services').insert([payload]).select().single();
        if (error) throw error;
        setServices(prev => [toCamelCase(data), ...prev]);
      }
      setIsSidebarOpen(false);
    } catch (err: any) {
      alert(`Sync failure: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirm('Permanently remove this service?')) return;
    try {
      const { error } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
      setServices(prev => prev.filter(s => s.id !== id));
      if (selectedServiceId === id) setSelectedServiceId(null);
    } catch (err: any) { alert(getErrorMessage(err)); }
  };

  return (
    <div className="animate-in fade-in duration-300">
      {selectedServiceId && selectedService ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <div className="flex items-center gap-4">
              <button onClick={() => setSelectedServiceId(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 transition-all"><ChevronLeft size={20} /></button>
              <div>
                <h3 className="text-lg font-bold text-slate-800 leading-none">{selectedService.name}</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1.5">Service Catalog Definition</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={(e) => handleOpenEdit(selectedService, e)} className="px-4 py-2 text-xs font-bold bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all flex items-center gap-2 shadow-sm"><Edit2 size={14} /> Modify</button>
              <button onClick={(e) => handleDelete(selectedService.id, e)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all shadow-sm"><Trash2 size={16} /></button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-6">
              <div className="bg-slate-900 rounded-xl p-8 text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 text-white/5 -rotate-12 pointer-events-none"><DollarSign size={120} /></div>
                <div className="relative z-10 space-y-6">
                  <div><h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Catalog Rate</h4><p className="text-5xl font-black tracking-tighter">${selectedService.basePrice.toLocaleString()}</p></div>
                </div>
              </div>

              <section className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Protocol Association</h4>
                {selectedService.agreementTemplateId ? (
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                    <ShieldCheck size={16} className="text-emerald-600 shrink-0" />
                    <p className="text-[11px] font-bold text-emerald-700 uppercase leading-none truncate">{templates.find(t => t.id === selectedService.agreementTemplateId)?.title || 'Mapped Protocol'}</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-100 rounded-xl"><X size={16} className="text-amber-600 shrink-0" /><p className="text-[11px] font-bold text-amber-700 leading-tight uppercase tracking-tighter">No Active Mapping</p></div>
                )}
              </section>
            </div>

            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white p-10 rounded-xl border border-slate-200 shadow-sm h-fit"><h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-6">Service Scope</h4><p className="text-slate-700 text-lg font-medium leading-relaxed whitespace-pre-wrap">{selectedService.description || "No specific scope definition provided."}</p></div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div><h3 className="text-xl font-bold text-slate-800 tracking-tight uppercase">Service Library</h3><p className="text-sm text-slate-500 font-medium">Standardized inspection catalog.</p></div>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:w-72"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input type="text" placeholder="Search catalog..." className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium focus:ring-1 focus:ring-[#606c38] outline-none shadow-sm transition-all" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
              <button onClick={handleOpenAdd} className="bg-slate-900 text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-sm hover:bg-slate-800 transition-all flex items-center gap-2 whitespace-nowrap"><Plus size={16} /> Add Service</button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-left">
              <thead><tr className="bg-slate-50/50 border-b border-slate-200"><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Service Artifact</th><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-center">Compliance</th><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Base Rate</th><th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(service => (
                  <tr key={service.id} onClick={() => setSelectedServiceId(service.id)} className="hover:bg-slate-50/80 transition-colors cursor-pointer group">
                    <td className="px-6 py-4 max-w-2xl"><div className="flex items-center gap-4"><div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-[#606c38]/10 group-hover:text-[#606c38] transition-colors shrink-0"><Briefcase size={18} /></div><div className="min-w-0"><p className="text-sm font-bold text-slate-800 truncate">{service.name}</p></div></div></td>
                    <td className="px-6 py-4 text-center">{service.agreementTemplateId ? <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full"><ShieldCheck size={10} /> Protocol Ready</span> : <span className="text-[9px] font-bold uppercase text-slate-300">No Protocol</span>}</td>
                    <td className="px-6 py-4 text-right"><span className="text-sm font-black text-slate-900 tracking-tighter">${service.basePrice.toLocaleString()}</span></td>
                    <td className="px-6 py-4 text-right"><div className="flex items-center justify-end gap-2"><button onClick={(e) => handleDelete(service.id, e)} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={16} /></button><ChevronRight size={16} className="text-slate-200" /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CRUD Sidebar */}
      <div className={`fixed inset-0 z-[300] ${isSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 transition-opacity duration-200" onClick={() => setIsSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest">{editingId ? 'Modify Service Definition' : 'Add Catalog Record'}</h3>
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white rounded-lg text-slate-400 transition-all"><X size={20} /></button>
          </div>
          <form onSubmit={handleSave} className="flex-1 p-8 space-y-12 overflow-y-auto no-scrollbar">
            <section className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-1.5"><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Service Headline</label><input required className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38]" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g. Standard Property Inspection" /></div>
                <div className="space-y-1.5"><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Base Rate ($)</label><input required type="number" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-black text-slate-800 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38]" value={formData.basePrice} onChange={(e) => setFormData({...formData, basePrice: parseFloat(e.target.value) || 0})} /></div>
                <div className="space-y-1.5"><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Native Protocol Provision</label><select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-800 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38]" value={formData.agreementTemplateId} onChange={(e) => setFormData({...formData, agreementTemplateId: e.target.value})}><option value="">No protocol provision</option>{templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}</select></div>
                <div className="space-y-1.5"><label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Scope Description</label><textarea required rows={4} className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-600 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38] resize-none" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Define service scope..." /></div>
              </div>
            </section>
          </form>
          <div className="p-6 border-t border-slate-100 flex gap-3 shadow-inner bg-white">
            <button type="button" onClick={() => setIsSidebarOpen(false)} className="flex-1 py-3 text-xs font-bold text-slate-500 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all uppercase tracking-widest">Cancel</button>
            <button disabled={saving} onClick={handleSave} className="flex-[2] py-3 bg-slate-900 text-white font-bold text-xs rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-sm uppercase tracking-widest">{saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Commit Record</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
